import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, ArrowLeft, Mail, User, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success(t('contact.success'));
    } catch {
      toast.error(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 bg-[#0A0A0A]">
        <Card className="w-full max-w-md bg-[#141414] border-white/10 rounded-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="font-heading text-2xl text-white mb-2">{t('contact.doneTitle')}</h2>
            <p className="text-zinc-500 mb-6">
              {t('contact.doneMessage')}<br />
              {t('contact.doneNote')}
            </p>
            <Button className="bg-white text-black hover:bg-zinc-200 rounded-sm" onClick={() => navigate('/')}>
              {t('contact.backHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" className="text-zinc-400 hover:text-white mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-2">{t('contact.title')}</h1>
          <p className="text-zinc-500">{t('contact.subtitle')}</p>
        </div>
        <Card className="bg-[#141414] border-white/10 rounded-sm">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-400 flex items-center gap-2"><User className="h-4 w-4" />{t('contact.name')}</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-[#0A0A0A] border-white/10 text-white rounded-sm" placeholder={t('contact.namePlaceholder')} data-testid="contact-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-400 flex items-center gap-2"><Mail className="h-4 w-4" />{t('contact.email')}</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="bg-[#0A0A0A] border-white/10 text-white rounded-sm" placeholder="email@example.com" data-testid="contact-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-zinc-400 flex items-center gap-2"><MessageSquare className="h-4 w-4" />{t('contact.subject')}</Label>
                <Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required className="bg-[#0A0A0A] border-white/10 text-white rounded-sm" placeholder={t('contact.subjectPlaceholder')} data-testid="contact-subject" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message" className="text-zinc-400">{t('contact.message')}</Label>
                <Textarea id="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={6} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm resize-none" placeholder={t('contact.messagePlaceholder')} data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm py-6" data-testid="contact-submit">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('contact.sending')}</> : <><Send className="h-4 w-4 mr-2" />{t('contact.send')}</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactPage;
